import { useState, useCallback } from "react";
import { INSTRUCTOR_EMPHASIS_CLASS } from "@/utils/quillEditorModules";

interface UseTextHighlightProps {
  contentType: "passage" | "question";
  onSaveHighlight?: (
    contentType: "passage" | "question",
    newContent: string
  ) => void;
}

const createHighlightSpan = (color: string): HTMLSpanElement => {
  const span = document.createElement("span");
  span.style.backgroundColor = color;
  span.className = "highlight-span";
  return span;
};

const rangeCrossesInstructorEmphasis = (range: Range): boolean => {
  const root = range.commonAncestorContainer;
  const rootEl =
    root.nodeType === Node.ELEMENT_NODE
      ? (root as HTMLElement)
      : root.parentElement;
  if (!rootEl) return false;

  // If the common ancestor itself is the only emphasis span, the range
  // is fully inside it — surroundContents will not throw on emphasis bounds.
  const emphasisNodes = rootEl.querySelectorAll(
    `.${INSTRUCTOR_EMPHASIS_CLASS}`
  );
  for (const node of Array.from(emphasisNodes)) {
    const startInside = node.contains(range.startContainer);
    const endInside = node.contains(range.endContainer);
    if (startInside !== endInside) return true;
  }
  return false;
};

// Wrap each text node within the range in its own highlight span, splitting
// partial-text edges. Used when surroundContents would throw because the
// range crosses an element boundary (typically .instructor-emphasis).
// Preserves nesting: an emphasis text node ends up as
// <span class="instructor-emphasis"><span class="highlight-span">...</span></span>.
const applyHighlightByTextNodes = (range: Range, color: string): void => {
  const root = range.commonAncestorContainer;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      range.intersectsNode(node)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  });

  const pieces: { node: Text; start: number; end: number }[] = [];
  let current = walker.nextNode() as Text | null;
  while (current) {
    let start = 0;
    let end = current.length;
    if (current === range.startContainer) start = range.startOffset;
    if (current === range.endContainer) end = range.endOffset;
    if (end > start) pieces.push({ node: current, start, end });
    current = walker.nextNode() as Text | null;
  }

  // Iterate in reverse so earlier offsets stay valid as we splitText later
  // pieces. Since text nodes are independent, splits never invalidate
  // earlier siblings.
  for (let i = pieces.length - 1; i >= 0; i--) {
    const { node, start, end } = pieces[i];
    try {
      let target = node;
      if (end < target.length) target.splitText(end);
      if (start > 0) target = target.splitText(start);
      const span = createHighlightSpan(color);
      target.parentNode?.insertBefore(span, target);
      span.appendChild(target);
    } catch (innerError) {
      console.warn(
        "Failed to highlight text node piece (skipping):",
        innerError
      );
    }
  }
};

const applyHighlightToRange = (range: Range, color: string): void => {
  // Fast path: if no instructor-emphasis boundary is crossed, the original
  // surroundContents works and yields the simplest markup.
  if (!rangeCrossesInstructorEmphasis(range)) {
    try {
      range.surroundContents(createHighlightSpan(color));
      return;
    } catch (error) {
      // Unknown cross-boundary case (other inline format, partial element).
      // Fall through to per-text-node fallback.
      console.warn(
        "surroundContents threw despite no emphasis crossing; falling back:",
        error
      );
    }
  }

  applyHighlightByTextNodes(range, color);
};

export const useTextHighlight = ({
  contentType,
  onSaveHighlight,
}: UseTextHighlightProps) => {
  // States
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerPosition, setColorPickerPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectedRange, setSelectedRange] = useState<Range | null>(null);

  // Text selection handler
  const handleTextSelected = useCallback(
    (selectedText: string, rect: DOMRect) => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        setSelectedRange(selection.getRangeAt(0).cloneRange());
      }

      setColorPickerPosition({
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY + 5,
      });
      setShowColorPicker(true);
    },
    []
  );

  // Close color picker handler
  const handleCloseColorPicker = useCallback(() => {
    setShowColorPicker(false);
    setSelectedRange(null);
  }, []);

  // Color selection handler
  const handleColorSelect = useCallback(
    (color: string) => {
      if (!selectedRange || !onSaveHighlight) return;

      try {
        // Apply highlight to range. Splits across .instructor-emphasis
        // boundaries when needed so the instructor span is preserved as the
        // outer wrapper (PLAN.md decision #13).
        applyHighlightToRange(selectedRange, color);

        // Get the updated content from the closest quill content container
        let node: Node | null = selectedRange.startContainer;
        while (node && !(node instanceof HTMLElement)) {
          node = node.parentElement;
        }

        let contentDiv: HTMLElement | null = node as HTMLElement | null;
        while (contentDiv && !contentDiv.classList.contains("quill-content")) {
          contentDiv = contentDiv.parentElement;
        }

        if (contentDiv && onSaveHighlight) {
          const newContent = contentDiv.innerHTML;
          onSaveHighlight(contentType, newContent);
        }
      } catch (error) {
        console.error("Failed to apply highlight:", error);
      }

      setShowColorPicker(false);
      setSelectedRange(null);
      window.getSelection()?.removeAllRanges();
    },
    [selectedRange, onSaveHighlight, contentType]
  );

  return {
    showColorPicker,
    colorPickerPosition,
    handleTextSelected,
    handleCloseColorPicker,
    handleColorSelect,
  };
};
